import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import useClassTeacher from "../hooks/useClassTeacher";
import useReference from "hooks/useReference";
import { getClassLevelLabelFromCode } from "utils/schoolUtils";
import TeacherClassesTable from "../table/TeacherClassesTable";

const TeacherClassesPage: React.FC = () => {
    const { t } = useTranslation();

    const teacherId = useSelector((state: any) => state.authSlice.user?.roleId);

    const {
        classes,
        loading,
        fetchTeacherClasses,
        pagination,
        error,
    } = useClassTeacher();

    const { fetchClassLevels, classLevels } = useReference();

    const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

    const breadcrumbItems = [
        { label: "dashboard", path: all_routes.dashboard },
        { label: "my_classes", active: true },
    ];

    useEffect(() => {
        if (teacherId) {
            const initialFilters = {
                page: 1,
                limit: 10,
            };

            setFilters(initialFilters);
            fetchTeacherClasses(initialFilters);
        }
        fetchClassLevels();
    }, [teacherId]);

    const classLevelOptions = useMemo(
        () =>
            (classLevels || [])?.map((level: any) => ({
                label: getClassLevelLabelFromCode(level.labelKey),
                value: level.id,
            })),
        [classLevels]
    );

    const filterFields: FilterField[] = useMemo(
        () => [
            {
                key: "classLevelId",
                label: t("level"),
                type: "select",
                options: classLevelOptions,
            },
        ],
        [t, classLevelOptions]
    );

    const handleFilter = (newFilters: { [key: string]: any }) => {
        const filtersWithDefaults = {
            ...newFilters,
            page: 1,
            limit: 10,
        };

        setFilters(filtersWithDefaults);
        fetchTeacherClasses(filtersWithDefaults);
    };

    const handleResetFilter = () => {
        const defaultFilters = {
            page: 1,
            limit: 10,
        };

        setFilters(defaultFilters);
        fetchTeacherClasses(defaultFilters);
    };

    const handleTableChange = (pg: any) => {
        const newFilters = {
            ...filters,
            page: pg.current!,
            limit: pg.pageSize!,
        };

        fetchTeacherClasses(newFilters);
        setFilters(newFilters);
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader title={t("my_classes")} breadcrumbItems={breadcrumbItems} />

                <div className="d-flex mb-3 justify-content-end">
                    <TableFilterDynamique
                        fields={filterFields}
                        filters={filters}
                        onApply={handleFilter}
                        onReset={handleResetFilter}
                    />
                </div>

                <TeacherClassesTable
                    classes={classes}
                    loading={loading}
                    pagination={pagination}
                    error={error}
                    onChange={handleTableChange}
                />
            </div>
        </div>
    );
};

export default TeacherClassesPage;
